/**
 * Axios 实例 + JWT 拦截器 + 401 静默续期/挂起重放
 *
 * 参考 docs/auth-token-refresh（ming-git 规范）：
 * access token 过期后不再"先弹 401、刷新/重点才正常"，而是：
 *   并发 401 入队 → 单飞 refresh（只一次）→ 成功后带新 token 重放原请求 → UI 无感知
 * 续期失败才统一清空登录态，由路由守卫(ProtectedRoute)兜底跳登录。
 *
 * 适配点（相对模板）：
 *   - baseURL：/api/v1（refresh 端点为 POST /auth/token，body { token }）
 *   - 登录流程(login/captcha/oauth/token)本身不续期，避免递归
 *   - token 直接读写 zustand store（useAuthStore），避免 localStorage 与状态脱节
 */
import axios, {
  type AxiosError,
  type AxiosResponse,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import type { ApiResponse, LoginResponse } from './types'
import { useAuthStore } from '@/store/auth'

// ── 两个 Axios 实例 ──
/** 业务请求：带 Bearer + 响应拦截器（含 401 静默续期） */
const client: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
})

/**
 * 无任何拦截器，专供 refresh 与"重放"使用。
 * 目的：refresh 不走进 401 拦截造成递归；重放也不触发二次续期。
 */
export const rawClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
})

// ── 请求拦截器:自动添加 Bearer token（从 store 实时读取，续期后即最新） ──
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ── API 错误类型 ──
export class ApiError extends Error {
  status: number
  message: string

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.message = message
  }
}

// ── 静默续期机制 ──

type Config = InternalAxiosRequestConfig

// 这些端点是"登录前/续期本身"用，401 不应触发续期（避免递归与语义错误）
const NO_REFRESH_PREFIXES = ['/auth/login', '/auth/captcha', '/auth/token', '/auth/oauth/', '/auth/login-config']
function isRefreshableUrl(url?: string): boolean {
  return !!url && !NO_REFRESH_PREFIXES.some((p) => url.startsWith(p))
}

let refreshGate: Promise<boolean> | null = null // 单飞 gate：多个并发 401 只 refresh 一次
let pendingReplays: Config[] = [] // 排队待重放
const replayMap = new WeakMap<Config, { resolve: (r: AxiosResponse) => void; reject: (e: unknown) => void }>() // config → 本请求结果通道
let marked = new WeakSet<Config>() // 判重；周期结束重建，防内存泄漏

/** 续期成功 → 重放全部排队请求，并把每个结果回填给对应等待者 */
function replayAll(): void {
  const batch = pendingReplays
  pendingReplays = []
  for (const cfg of batch) {
    const waiters = replayMap.get(cfg)
    const request = (async () => {
      const newToken = useAuthStore.getState().accessToken
      if (newToken) {
        cfg.headers = cfg.headers ?? {}
        cfg.headers.Authorization = `Bearer ${newToken}`
      }
      return await rawClient.request(cfg) // 走 rawClient，不触发拦截器
    })()
    request.then((r) => waiters?.resolve(r), (e) => waiters?.reject(e))
  }
}

/** 排他续期一次：用 refresh token 换新 access + refresh token */
async function doRefresh(): Promise<boolean> {
  const refreshToken = useAuthStore.getState().refreshToken
  if (!refreshToken) return false
  try {
    const { data } = await rawClient.post<ApiResponse<LoginResponse>>('/auth/token', { token: refreshToken })
    if (data?.success && data.data) {
      const payload = data.data
      const prevUser = useAuthStore.getState().user
      useAuthStore.setState({
        // refresh 只返回 {id, username}，合并保留原 user 的 is_admin/email 等字段
        user: { ...(prevUser ?? {}), ...payload.user } as never,
        accessToken: payload.token,
        refreshToken: payload.refresh_token,
      })
      return true
    }
    return false
  } catch {
    // 续期失败（refresh 也过期/失效）→ 清空登录态，由路由守卫跳登录
    useAuthStore.setState({ user: null, accessToken: null, refreshToken: null })
    return false
  }
}

/** 401 入口：入队 →（必要时）启动单飞续期 → 返回本请求自身重放结果 */
function enterRefreshAndReplay(cfg: Config): Promise<AxiosResponse> {
  return new Promise((resolve, reject) => {
    if (!marked.has(cfg)) {
      marked.add(cfg)
      pendingReplays.push(cfg)
      replayMap.set(cfg, { resolve, reject })
    }
    if (!refreshGate) {
      refreshGate = doRefresh()
        .then((ok) => {
          if (ok) replayAll()
          return ok
        })
        .finally(() => {
          refreshGate = null
          // 续期失败仍排队中的请求 → 统一 reject 为 401，由 UI 守卫跳登录
          const failed = pendingReplays
          pendingReplays = []
          marked = new WeakSet()
          for (const cfg of failed) {
            const w = replayMap.get(cfg)
            w?.reject(new ApiError(401, '登录已过期，请重新登录'))
            replayMap.delete(cfg)
          }
        })
    }
  })
}

// ── 响应拦截器:统一错误处理 ──
client.interceptors.response.use(
  (response) => {
    const data = response.data as ApiResponse<unknown>
    // 后端返回 success: false 时包装为错误
    if (data && data.success === false) {
      const msg = data.error || '请求失败'
      return Promise.reject(new ApiError(response.status, msg))
    }
    return response
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    // 网络错误
    if (!error.response) {
      return Promise.reject(new ApiError(0, '网络错误，无法连接到服务器'))
    }

    const status = error.response.status
    const cfg = error.config

    // 401 且可续期 → 挂起静默续期 + 重放，期间不 reject（UI 收不到 401）
    if (status === 401 && cfg && isRefreshableUrl(cfg.url)) {
      return enterRefreshAndReplay(cfg)
    }

    // 真正的失败（登录/续期本身 401、403、5xx 等）→ 正常抛出给 UI
    const msg = error.response.data?.error || explainError(status)
    return Promise.reject(new ApiError(status, msg))
  },
)

// ── 错误码映射为中文 ──
export function explainError(status: number): string {
  switch (status) {
    case 400: return '请求参数错误'
    case 401: return '未登录或登录已过期'
    case 403: return '没有权限执行此操作'
    case 404: return '请求的资源不存在'
    case 409: return '资源冲突'
    case 429: return '请求过于频繁，请稍后重试'
    case 500: return '服务器内部错误'
    case 502: return '网关错误'
    case 503: return '服务不可用'
    case 504: return '网关超时'
    default: return `请求失败 (${status})`
  }
}

// ── 提取 data ──
export async function unwrap<T>(
  promise: Promise<AxiosResponse<ApiResponse<T>>>,
): Promise<T> {
  const response = await promise
  const body = response.data
  if (body.success && body.data !== null) {
    return body.data
  }
  throw new ApiError(0, body.error || '响应数据为空')
}

export default client